// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * RoleBanner - Shows current player's role prominently
 *
 * A Focus Mode component that clearly indicates the player's current role
 * and optionally what action they should take.
 *
 * Generalized for use across all games - each game can define its own
 * role configurations.
 */

import { memo } from 'react';
import { m, AnimatePresence } from 'framer-motion';

export interface RoleConfig {
  icon: string;
  title: string;
  color: string;
}

interface RoleBannerProps {
  /** Role configuration with icon, title, and color */
  role: RoleConfig;
  /** Optional action text to show below the role */
  action?: string;
  /** Optional prefix text (default: "YOU ARE THE") */
  prefix?: string;
  /** Custom class names */
  className?: string;
  /** Disable animations */
  disableAnimation?: boolean;
}

export const RoleBanner = memo(function RoleBanner({
  role,
  action,
  prefix = 'YOU ARE THE',
  className = '',
  disableAnimation = false,
}: RoleBannerProps) {
  const content = (
    <div
      className="rounded-neo-sm px-6 py-4 text-center"
      style={{
        backgroundColor: 'var(--color-surface)',
        border: 'var(--neo-border-medium)',
        borderColor: role.color,
        boxShadow: 'var(--neo-shadow-md)',
      }}
    >
      <p
        className="text-sm font-medium uppercase tracking-wider mb-1"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {prefix}
      </p>
      <div className="flex items-center justify-center gap-2">
        <span className="text-3xl" aria-hidden="true">
          {role.icon}
        </span>
        <span
          className="text-2xl font-display font-bold tracking-wide"
          style={{ color: role.color }}
        >
          {role.title}
        </span>
      </div>
      {action && (
        <p className="mt-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {action}
        </p>
      )}
    </div>
  );

  if (disableAnimation) {
    return (
      <div className={`flex flex-col items-center justify-center py-4 ${className}`}>{content}</div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <m.div
        key={role.title}
        className={`flex flex-col items-center justify-center py-4 ${className}`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        {content}
      </m.div>
    </AnimatePresence>
  );
});

// Pre-defined role configurations for common game patterns
export const CommonRoles = {
  // Judge-style roles
  judge: {
    icon: '⚖️',
    title: 'JUDGE',
    color: 'var(--color-primary)',
  },
  host: {
    icon: '👑',
    title: 'HOST',
    color: 'var(--color-warning)',
  },

  // Player-style roles
  player: {
    icon: '🎮',
    title: 'PLAYER',
    color: 'var(--color-accent)',
  },
  contestant: {
    icon: '🏆',
    title: 'CONTESTANT',
    color: 'var(--color-accent)',
  },

  // Terrible Psychiatrist specific
  psychiatrist: {
    icon: '🩺',
    title: 'PSYCHIATRIST',
    color: 'var(--color-primary)',
  },
  patient: {
    icon: '💊',
    title: 'PATIENT',
    color: 'var(--color-accent)',
  },

  // Debate Club specific
  debater: {
    icon: '🎤',
    title: 'DEBATER',
    color: 'var(--color-accent)',
  },
  proSide: {
    icon: '👍',
    title: 'PRO',
    color: 'var(--color-success)',
  },
  conSide: {
    icon: '👎',
    title: 'CON',
    color: 'var(--color-error)',
  },

  // Sick Day specific
  boss: {
    icon: '👔',
    title: 'BOSS',
    color: 'var(--color-primary)',
  },
  employee: {
    icon: '🤒',
    title: 'EMPLOYEE',
    color: 'var(--color-accent)',
  },

  // Peer Review specific
  reviewer: {
    icon: '📝',
    title: 'REVIEWER',
    color: 'var(--color-primary)',
  },
  author: {
    icon: '✍️',
    title: 'AUTHOR',
    color: 'var(--color-accent)',
  },

  // Generic waiting/spectating
  spectator: {
    icon: '👁️',
    title: 'SPECTATOR',
    color: 'var(--color-text-muted)',
  },
  waiting: {
    icon: '⏳',
    title: 'WAITING',
    color: 'var(--color-text-muted)',
  },
} as const;

export default RoleBanner;
